/* eslint-disable react/display-name */
import React, { memo, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { withScreenSize } from '../common/utilities/withScreenSize.jsx';
import { reduxConnect } from '../../common';
import { dataLayer } from '@weatherbug/common';
const { getPollenByLatLon } = dataLayer.actions;
import { PollenCount } from './pollenCount.js';
import { PollenForecast } from './pollenForecast.js';
import { getPollenDescriptors } from './pollenDescriptors';
import { translate } from  '../../common/translation/translate.js';
import { LifeStories } from '../common/life/lifeStories.js';
import { LifePageContentTitle } from '../common/life/lifePageContentTitle';
import { LifeHeaderText } from '../common/life/lifeIntroText';
import { DisplayAdWidget } from '../ads/displayAdWidget.jsx';
import { mediumMediaQuery, largeMediaQuery } from '../../common/utilities/sizeHelpers';
import { getNewsStories } from '../../common/actions/stories/storiesAction';
import { DisplayAdUnit } from '../../containers/ads/displayAdUnitContainer';

const source = 'life/pollen';
const allergyForecastAffilliateId = 6899;

const mapStoreStateToProps = storeState => {
    const { pollen, newsStories } =  storeState;
    const appState = storeState.appState && storeState.appState.appState || {};
    const { authId, enableSourceParameterOnStoriesLinks, locality, pollenBaseUrl, secret, stdParameters } = appState;
    const pollenResult = pollen?.pollen;

    return {
        latitude: locality?.Latitude,
        longitude: locality?.Longitude,
        pollenIndex: pollenResult?.pollenIndex,
        predominantPollen: pollenResult?.predominantPollen,
        pollenTechDiscussionToday: pollenResult?.techDiscussionToday,
        pollenTechDiscussion: pollenResult?.techDiscussion,
        authId,
        secret,
        pollenBaseUrl,
        stdParameters,
        pollenStories: newsStories?.stories,
        lastError: newsStories?.lastError,
        localityLabel: locality?.Label,
        localityZip: locality?.PostalCode,
        enableSourceParameterOnStoriesLinks
    };
};

const mapDispatchToProps = {
    getPollenByLatLon,
    getNewsStories
};

const PollenPage = withScreenSize(reduxConnect(mapStoreStateToProps, mapDispatchToProps) (memo(({
    latitude,
    longitude,
    pollenStories,
    pollenIndex = 0,
    predominantPollen,
    pollenTechDiscussionToday,
    pollenTechDiscussion,
    getPollenByLatLon,
    localityLabel='',
    authId,
    secret,
    pollenBaseUrl,
    stdParameters,
    localityZip,
    getNewsStories,
    isDesktop,
    isMobile,
    enableSourceParameterOnStoriesLinks
}) => {

    const { color: pollenColor = 'transparent', description: pollenDescription } = getPollenDescriptors(pollenIndex);
    const introText = `${translate('pollenForecastFor')} ${localityLabel}`;
    const affilliateLink = new URL('https://www.pollen.com/allergy-forecast.asp');
    if (localityZip) {
        affilliateLink.searchParams.append('zip', localityZip);
    }
    affilliateLink.searchParams.append('affiliateid', allergyForecastAffilliateId);
    const poweredByPollenLink = affilliateLink.toString();

    const { mainStory, subStoriesList } = useMemo(() => {
        const [mainStory, ...restSubStoriesList] = pollenStories; 
        return { 
            mainStory, 
            subStoriesList: restSubStoriesList 
        }; 
    },
    [
        pollenStories?.length,
        pollenStories?.[0]?.Id,
        pollenStories?.[1]?.Id,
        pollenStories?.[2]?.Id,
        pollenStories?.[3]?.Id,
        pollenStories?.[4]?.Id
    ]);

    useEffect(() => {
        window.scrollTo(0, 0);
        getNewsStories({ baseUrl: location.origin, category: 'Pollen', count: 6 });
    }, []);

    useEffect(() => {
        if (latitude && longitude && authId && secret && pollenBaseUrl) {
            getPollenByLatLon(
                latitude,
                longitude,
                pollenBaseUrl,
                authId,
                secret,
                stdParameters,
                true
            );
        }
    }, [latitude, longitude, authId, secret, pollenBaseUrl]);

    return (
        <>
            {!isDesktop
                && <MobileBannerAd>
                    <DisplayAdUnit
                        width={320}
                        height={50}
                        slotId={'life_pollen_mobile_top_banner_x83'}
                    />
                </MobileBannerAd>
            }
            <LifePageContentTitle pageTitle={translate('allergiesAndPollen')} />
            <LifeHeaderText>{introText}</LifeHeaderText>
            <PageContainer>
                <ContentContainer>
                    <PollenCount
                        pollenColor={pollenColor}
                        pollenIndex={pollenIndex}
                        pollenDescription={pollenDescription}
                        predominantPollen={predominantPollen}
                        poweredByPollenLink={poweredByPollenLink}
                    />
                    <PollenForecast
                        pollenTechDiscussionToday={pollenTechDiscussionToday}
                        pollenTechDiscussion={pollenTechDiscussion}
                    />
                    <LifeStories
                        mainStory={mainStory}
                        subStoriesList={subStoriesList}
                        slotId={'life_pollen_mobile_stories_x11'}
                        isMobile={isMobile}
                        enableSourceParameterOnStoriesLinks={enableSourceParameterOnStoriesLinks}
                        source={source}
                    />
                </ContentContainer>
                {isDesktop
                    && <AdContainer>
                        <DisplayAdUnit
                            slotId={'life_pollen_top_right_x16'}
                            height={600}
                            width={300}
                        />
                    </AdContainer>
                }
            </PageContainer>
        </>
    );
})));

const PageContainer = styled.div`
    display: flex;
    flex-direction: row;
    @media ${largeMediaQuery} {
        margin-right: 5px;
    }
`;

const MobileBannerAd = styled.div`
    margin-bottom: 20px;
`;

const ContentContainer = styled.div`
    margin-top: 10px;
    width: 100%;

    @media ${largeMediaQuery} {
        width: calc(100% - 316px);
    }

    @media ${mediumMediaQuery} {
        margin-bottom: 10px;
    }
`;

const AdContainer = styled.div`
    padding: 10px 0 0 16px;
`;

export { PollenPage as default };
